import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const dashboardItems = [
  {
    label: '总分趋势',
    desc: '一所学校整体总分分布随时间的变化',
    icon: 'ri-line-chart-line',
    color: 'primary' as const,
    chart: 'line',
    metric: '+23%',
    metricLabel: '同比提升',
  },
  {
    label: '分档比例',
    desc: '优秀、良好、合格、需重点支持的比例',
    icon: 'ri-pie-chart-line',
    color: 'accent' as const,
    chart: 'pie',
    metric: '4档',
    metricLabel: '分层统计',
  },
  {
    label: '主题覆盖',
    desc: '哪些课程主题覆盖充分，哪些薄弱',
    icon: 'ri-radar-line',
    color: 'primary' as const,
    chart: 'radar',
    metric: '85%',
    metricLabel: '覆盖率',
  },
  {
    label: '质量热力图',
    desc: '一校一图、一省一图的质量结构可视化',
    icon: 'ri-map-2-line',
    color: 'accent' as const,
    chart: 'heatmap',
    metric: '6维',
    metricLabel: '多维对比',
  },
  {
    label: '教师成长',
    desc: '哪些教师成长明显，哪些教研组需支持',
    icon: 'ri-user-star-line',
    color: 'primary' as const,
    chart: 'bar',
    metric: '18人',
    metricLabel: '成长明显',
  },
  {
    label: '资源投向',
    desc: '基于数据的资源配置优化建议',
    icon: 'ri-funds-line',
    color: 'accent' as const,
    chart: 'arrow',
    metric: 'AI',
    metricLabel: '智能推荐',
  },
];

function MiniLineChart({ color }: { color: 'primary' | 'accent' }) {
  const c = color === 'accent' ? 'var(--accent-400)' : 'var(--primary-400)';
  return (
    <svg viewBox="0 0 200 64" className="w-full h-16">
      <defs>
        <linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c} stopOpacity="0.3" />
          <stop offset="100%" stopColor={c} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path
        d="M0,52 C20,48 40,42 60,40 80,38 100,28 120,24 140,20 160,14 180,10 L200,6 L200,64 L0,64 Z"
        fill={`url(#grad-${color})`}
      />
      <polyline
        points="0,52 20,48 40,42 60,40 80,38 100,28 120,24 140,20 160,14 180,10 200,6"
        fill="none"
        stroke={c}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="200" cy="6" r="4" fill={c} />
      <circle cx="200" cy="6" r="7" fill={c} fillOpacity="0.15" />
    </svg>
  );
}

function MiniPieChart({ color }: { color: 'primary' | 'accent' }) {
  const c = color === 'accent' ? 'var(--accent-400)' : 'var(--primary-400)';
  const c2 = color === 'accent' ? 'var(--accent-300)' : 'var(--primary-300)';
  const c3 = color === 'accent' ? 'var(--accent-200)' : 'var(--primary-200)';
  const c4 = color === 'accent' ? 'var(--accent-100)' : 'var(--primary-100)';
  const r = 28;
  const circ = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 80 64" className="w-full h-16">
      <circle cx="40" cy="32" r={r} fill="none" stroke={c4} strokeWidth="9" />
      <circle cx="40" cy="32" r={r} fill="none" stroke={c3} strokeWidth="9" strokeDasharray={`${circ * 0.35} ${circ}`} strokeDashoffset="0" />
      <circle cx="40" cy="32" r={r} fill="none" stroke={c2} strokeWidth="9" strokeDasharray={`${circ * 0.28} ${circ}`} strokeDashoffset={-circ * 0.35} />
      <circle cx="40" cy="32" r={r} fill="none" stroke={c} strokeWidth="9" strokeDasharray={`${circ * 0.22} ${circ}`} strokeDashoffset={-circ * 0.63} />
    </svg>
  );
}

function MiniRadarChart({ color }: { color: 'primary' | 'accent' }) {
  const c = color === 'accent' ? 'var(--accent-400)' : 'var(--primary-400)';
  const c2 = color === 'accent' ? 'var(--accent-200)' : 'var(--primary-200)';
  return (
    <svg viewBox="0 0 100 64" className="w-full h-16">
      <g transform="translate(50,32)">
        {[1, 2, 3, 4].map((i) => (
          <polygon
            key={i}
            points={`${30 * i / 4},0 ${15 * i / 4},${26 * i / 4} -${15 * i / 4},${26 * i / 4} -${30 * i / 4},0 -${15 * i / 4},-${26 * i / 4} ${15 * i / 4},-${26 * i / 4}`}
            fill="none"
            stroke={c2}
            strokeWidth="0.8"
            strokeOpacity={0.3 + i * 0.1}
          />
        ))}
        <polygon points="25,-5 18,12 5,16 -12,8 -20,-8 -8,-18 10,-14" fill={c} fillOpacity="0.2" stroke={c} strokeWidth="1.5" strokeLinejoin="round" />
        {[
          [25, -5], [18, 12], [5, 16], [-12, 8], [-20, -8], [-8, -18], [10, -14],
        ].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="2.5" fill={c} />
        ))}
      </g>
    </svg>
  );
}

function MiniHeatmap({ color }: { color: 'primary' | 'accent' }) {
  const c = color === 'accent' ? 'var(--accent-400)' : 'var(--primary-400)';
  const c2 = color === 'accent' ? 'var(--accent-300)' : 'var(--primary-300)';
  const c3 = color === 'accent' ? 'var(--accent-200)' : 'var(--primary-200)';
  const c4 = color === 'accent' ? 'var(--accent-100)' : 'var(--primary-100)';
  const palette = [c4, c3, c2, c, c2, c3, c4, c3, c, c2, c, c3, c4, c2];
  const rows = 4;
  const cols = 7;
  const w = 12;
  const h = 10;
  const gap = 2;
  const totalW = cols * (w + gap) - gap;
  const startX = (100 - totalW) / 2;
  return (
    <svg viewBox="0 0 100 64" className="w-full h-16">
      {Array.from({ length: rows * cols }).map((_, i) => {
        const row = Math.floor(i / cols);
        const col = i % cols;
        return (
          <rect
            key={i}
            x={startX + col * (w + gap)}
            y={8 + row * (h + gap)}
            width={w}
            height={h}
            rx="2"
            fill={palette[i % palette.length]}
            fillOpacity={0.5 + ((i + row * 3) % 5) * 0.12}
          />
        );
      })}
    </svg>
  );
}

function MiniBarChart({ color }: { color: 'primary' | 'accent' }) {
  const c = color === 'accent' ? 'var(--accent-400)' : 'var(--primary-400)';
  const c2 = color === 'accent' ? 'var(--accent-300)' : 'var(--primary-300)';
  const bars = [22, 18, 30, 26, 38, 32, 48];
  const bw = 10;
  const gap = 3;
  const totalW = bars.length * (bw + gap) - gap;
  const startX = (100 - totalW) / 2;
  return (
    <svg viewBox="0 0 100 64" className="w-full h-16">
      {bars.map((h, i) => (
        <g key={i}>
          <rect
            x={startX + i * (bw + gap)}
            y={58 - h}
            width={bw}
            height={h}
            rx="3"
            fill={i >= bars.length - 2 ? c : c2}
          />
          {i >= bars.length - 2 && (
            <rect
              x={startX + i * (bw + gap)}
              y={58 - h}
              width={bw}
              height={h}
              rx="3"
              fill={c}
              fillOpacity="0.15"
            />
          )}
        </g>
      ))}
    </svg>
  );
}

function MiniArrowFlow({ color }: { color: 'primary' | 'accent' }) {
  const c = color === 'accent' ? 'var(--accent-400)' : 'var(--primary-400)';
  return (
    <svg viewBox="0 0 160 64" className="w-full h-16">
      <path d="M10 32 L110 32 L102 24 M110 32 L102 40" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <rect x="120" y="16" width="32" height="32" rx="8" fill={c} fillOpacity="0.1" stroke={c} strokeWidth="1.5" />
      <text x="136" y="37" textAnchor="middle" fontSize="13" fill={c} fontWeight="bold">AI</text>
    </svg>
  );
}

const ChartMap: Record<string, React.FC<{ color: 'primary' | 'accent' }>> = {
  line: MiniLineChart,
  pie: MiniPieChart,
  radar: MiniRadarChart,
  heatmap: MiniHeatmap,
  bar: MiniBarChart,
  arrow: MiniArrowFlow,
};

export default function P20EvidenceManager({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-10 md:mb-12">
          <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            服务管理者
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            证据<span className="text-primary-600">对管理者</span>的价值
          </h2>
          <p className={`text-base text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            AI提供的是质量趋势和资源配置依据，不需要逐节课看视频
          </p>
        </div>

        {/* Dashboard mockup - 全宽仪表盘 */}
        <div className={`max-w-6xl mx-auto mb-10 rounded-2xl overflow-hidden transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* 顶部标题栏 */}
          <div className="bg-background-50 border border-background-200/50 px-5 py-3 rounded-t-2xl flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-300/60" />
              <div className="w-3 h-3 rounded-full bg-yellow-300/60" />
              <div className="w-3 h-3 rounded-full bg-green-300/60" />
            </div>
            <span className="text-xs font-mono text-foreground-400 ml-2">管理者仪表盘 · 质量概览</span>
          </div>

          {/* 主内容区域：背景网格 + 卡片 */}
          <div
            className="relative border-x border-b border-background-200/50 rounded-b-2xl p-6 md:p-10"
            style={{
              background: `
                linear-gradient(rgba(248,250,252,0.85), rgba(248,250,252,0.85)),
                linear-gradient(90deg, rgba(100,149,237,0.05) 1px, transparent 1px),
                linear-gradient(rgba(100,149,237,0.05) 1px, transparent 1px)
              `,
              backgroundSize: '100% 100%, 40px 40px, 40px 40px',
            }}
          >
            {/* 装饰数据流线条 */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.04]" preserveAspectRatio="none">
              <path d="M0 50 Q 300 20 600 80 T 1200 40" fill="none" stroke="var(--primary-400)" strokeWidth="1.5" />
              <path d="M0 150 Q 400 100 800 180 T 1400 120" fill="none" stroke="var(--accent-400)" strokeWidth="1" />
            </svg>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 relative z-10">
              {dashboardItems.map((item, i) => {
                const ChartComponent = ChartMap[item.chart];
                const isAccent = item.color === 'accent';
                return (
                  <div
                    key={item.label}
                    className={`group relative rounded-xl bg-white/80 border transition-all duration-500 hover:-translate-y-1 hover:shadow-lg overflow-hidden ${
                      isAccent
                        ? 'border-accent-200/50 hover:border-accent-300/70 hover:shadow-accent-100/15'
                        : 'border-primary-200/50 hover:border-primary-300/70 hover:shadow-primary-100/15'
                    }`}
                    style={{ transitionDelay: `${300 + i * 80}ms` }}
                  >
                    {/* 顶部色条 */}
                    <div className={`h-1 w-full ${isAccent ? 'bg-accent-400' : 'bg-primary-400'}`} />

                    {/* 头部：图标 + 标题 + 指标 */}
                    <div className="p-5 pb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isAccent
                            ? 'bg-accent-50 border border-accent-200/40'
                            : 'bg-primary-50 border border-primary-200/40'
                        }`}>
                          <i className={`${item.icon} text-base ${isAccent ? 'text-accent-600' : 'text-primary-600'}`} />
                        </div>
                        <p className="text-sm font-body font-bold text-foreground-800">{item.label}</p>
                      </div>
                      <div className="text-right">
                        <span className={`number-font text-lg font-bold leading-none ${isAccent ? 'text-accent-600' : 'text-primary-600'}`}>
                          {item.metric}
                        </span>
                        <p className="text-[10px] text-foreground-400 font-body mt-0.5">{item.metricLabel}</p>
                      </div>
                    </div>

                    {/* 迷你数据图表 */}
                    <div className="px-5">
                      <ChartComponent color={item.color} />
                    </div>

                    {/* 底部描述 */}
                    <div className="px-5 pb-5 pt-2">
                      <div className={`h-px w-full mb-3 ${isAccent ? 'bg-accent-100' : 'bg-primary-100'}`} />
                      <p className="text-xs text-foreground-500 font-body leading-relaxed">{item.desc}</p>
                    </div>

                    {/* 悬浮装饰光点 */}
                    <div className={`absolute top-3 right-3 w-1.5 h-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${isAccent ? 'bg-accent-400' : 'bg-primary-400'}`} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* 底部总结 */}
        <div className={`max-w-2xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="relative p-6 rounded-2xl glass-gold border-accent-200/30 overflow-hidden">
            {/* 背景装饰弧线 */}
            <svg className="absolute -right-6 -top-6 w-24 h-24 text-accent-200/20" viewBox="0 0 100 100">
              <path d="M50 0 A50 50 0 0 1 100 50" fill="none" stroke="currentColor" strokeWidth="8" />
            </svg>
            <p className="relative z-10 text-sm text-foreground-600 font-body leading-relaxed">
              管理者看到的<strong className="text-primary-700">不是单节课细节</strong>，而是<strong className="text-accent-700">一校一图、一省一图</strong>里的质量结构。
              这种能力，正是大规模常态化评价中<strong className="text-primary-700">人工方式最难完成</strong>的部分。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}